"use client";
import { useEffect } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { editTodosForm } from "@/hooks/ReactQueryHooks";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomInput from "@/components/customInput/CustomInput";
import CustomCalendar from "@/components/customCalendar/CustomCalendar";

interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  onChange: (value: any) => void;
  showModalEdit: any;
  selectedTodosId: any;
  description: any;
  position: any;
  is_completed: any;

}
type Inputs = {
  title: string;
  description: string;
  todo_date: string;
  is_completed: string;
  position: string;
  priority: "";

};

export default function EditTodos({
  showModalEdit,
  onClose,
  selectedTodosId,
}: TaskModalProps) {
  if (!showModalEdit) return null;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  useEffect(() => {
    if (selectedTodosId) {
      reset({
        title: selectedTodosId?.title || "",
        description: selectedTodosId?.description || "",
        priority: selectedTodosId?.priority || "",
        todo_date: selectedTodosId?.todo_date || "",
        position: selectedTodosId?.position || "",
      });
    }
  }, [selectedTodosId, reset]);



  const mutation = useMutation({
    mutationFn: (data: Inputs) => editTodosForm(selectedTodosId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todoLists"] });
      toast.success("Todo updated successfully!");
      onClose();
    },
    onError: () => {
      toast.error("Failed to update todo");
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    mutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 min-h-screen">
      <div className="bg-white w-full max-w-xl rounded-xl shadow-lg p-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[16px] font-inter font-semibold text-black">
            Add New Task
          </h2>
          <button
            onClick={onClose}
            className="text-[14px] font-inter font-semibold text-black"
          >
            Go Back
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Title */}
          <div className="mb-4">
            <CustomInput<Inputs>
              name="title"
              label="Title"
              register={register}

            />
          </div>
          <div className="mb-4 hidden">
            <CustomInput<Inputs>
              name="is_completed"
              label="Is Completed"
              register={register}
              defaultValue="true"
            />
          </div>
          <div className="mb-4 hidden">
            <CustomInput<Inputs>
              name="position"
              label="position"
              register={register}
              defaultValue="true"
            />
          </div>

          {/* Date */}
          <div className="mb-4">
            <label className="block text-[14px] font-inter font-medium text-[#0C0C0C] mb-1">
              Date
            </label>
            <div>
              <Controller
                name="todo_date"
                control={control}
                render={({ field }) => (
                  <CustomCalendar
                    value={field.value || ""}
                    onChange={(dateString) => field.onChange(dateString)}
                  />
                )}
              />
            </div>
          </div>

          {/* Priority */}
          <div className="mb-4">
            <label className="block text-[14px] font-inter font-medium text-[#0C0C0C]  mb-2">
              Priority
            </label>
            <div className="sm:flex block items-center gap-12 text-[14px]">
              <div className="flex items-center gap-1 cursor-pointer">
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                <span className="text-[13px] font-inter font-normal text-[#4B5563] ">
                  Extreme
                </span>
                <input
                  type="radio"
                  className="ml-1"
                  value="extreme"
                  {...register("priority")}
                />
              </div>

              <div className="flex items-center gap-1 cursor-pointer">
                <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                <span className="text-[13px] font-inter font-normal text-[#4B5563] ">
                  Moderate
                </span>
                <input
                  type="radio"
                  className="ml-1"
                  value="moderate"
                  {...register("priority")}
                />
              </div>

              <div className="flex items-center gap-1 cursor-pointer">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                <span className="text-[13px] font-inter font-normal text-[#4B5563] ">
                  Low
                </span>
                <input
                  type="radio"
                  className="ml-1"
                  value="low"
                  {...register("priority")}
                />
              </div>
            </div>
          </div>

          {/* Task Description */}
          <div className="mb-6">
            <CustomInput<Inputs>
              name="description"
              label="Task Description"
              placeholder="Start writing here"
              type="textarea"
              rows={6}
              register={register}
              errors={errors}
            />
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-between mt-4">
            <button className="bg-[#5272FF] text-white px-6 py-1.5 rounded-md text-[14px] font-inter font-semibold hover:bg-[#5272FF]">
              Done
            </button>

            <button
              className="bg-[#EE0039] text-white p-2 rounded-lg hover:bg-[#EE0039]"
              onClick={onClose}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
